/**
 * Serviço de Auditoria
 *
 * Este serviço gerencia as chamadas para a API de auditoria
 */

import { apiRequest } from "./apiService";

export interface AuditLog {
  id: string;
  user_id: string | null;
  action: string;
  action_type_id: number;
  action_type_name: string;
  details: string | null;
  ip_address: string;
  created_at: string;
  user_email: string | null;
  user_name: string | null;
  client_identifier?: string | null;
  client_name?: string | null;
  client_company?: string | null;
}

export interface ActionType {
  id: number;
  name: string;
  description: string;
}

export interface AuditStats {
  total_actions: number;
  actions_by_type: Array<{
    action_type: string;
    count: number;
  }>;
  actions_by_day: Array<{
    date: string;
    count: number;
  }>;
}

export interface AuditFilters {
  limit?: number;
  offset?: number;
  actionType?: string;
  startDate?: string;
  endDate?: string;
  userId?: string;
}

export interface PaginationInfo {
  limit: number;
  offset: number;
  total: number;
}

export interface AuditResponse {
  success: boolean;
  data: {
    logs: AuditLog[];
    pagination: PaginationInfo;
  };
  message: string;
}

export interface AuditStatsResponse {
  success: boolean;
  data: AuditStats;
  message: string;
}

export interface ActionTypesResponse {
  success: boolean;
  data: ActionType[];
  message: string;
}

/**
 * Obtém logs de auditoria do usuário logado
 */
export async function getUserAuditLogs(
  filters: AuditFilters = {}
): Promise<AuditResponse> {
  const params = new URLSearchParams();

  if (filters.limit) params.append("limit", filters.limit.toString());
  if (filters.offset) params.append("offset", filters.offset.toString());
  if (filters.actionType) params.append("actionType", filters.actionType);
  if (filters.startDate) params.append("startDate", filters.startDate);
  if (filters.endDate) params.append("endDate", filters.endDate);

  const queryString = params.toString();
  const url = `/audit/user${queryString ? `?${queryString}` : ""}`;

  return await apiRequest<AuditResponse>(url, "GET");
}

/**
 * Obtém logs de auditoria de um usuário específico
 */
export async function getUserAuditLogsById(
  userId: string,
  filters: AuditFilters = {}
): Promise<AuditResponse> {
  const params = new URLSearchParams();

  if (filters.limit) params.append("limit", filters.limit.toString());
  if (filters.offset) params.append("offset", filters.offset.toString());
  if (filters.actionType) params.append("actionType", filters.actionType);
  if (filters.startDate) params.append("startDate", filters.startDate);
  if (filters.endDate) params.append("endDate", filters.endDate);

  const queryString = params.toString();
  const url = `/audit/user/${userId}${queryString ? `?${queryString}` : ""}`;

  return await apiRequest<AuditResponse>(url, "GET");
}

/**
 * Obtém todos os logs de auditoria (apenas para administradores)
 */
export async function getAllAuditLogs(
  filters: AuditFilters = {}
): Promise<AuditResponse> {
  const params = new URLSearchParams();

  if (filters.limit) params.append("limit", filters.limit.toString());
  if (filters.offset) params.append("offset", filters.offset.toString());
  if (filters.actionType) params.append("actionType", filters.actionType);
  if (filters.userId) params.append("userId", filters.userId);
  if (filters.startDate) params.append("startDate", filters.startDate);
  if (filters.endDate) params.append("endDate", filters.endDate);

  const queryString = params.toString();
  const url = `/audit/all${queryString ? `?${queryString}` : ""}`;

  return await apiRequest<AuditResponse>(url, "GET");
}

/**
 * Obtém estatísticas de auditoria
 */
export async function getAuditStatistics(
  filters: Omit<AuditFilters, "limit" | "offset"> = {}
): Promise<AuditStatsResponse> {
  const params = new URLSearchParams();

  if (filters.actionType) params.append("actionType", filters.actionType);
  if (filters.userId) params.append("userId", filters.userId);
  if (filters.startDate) params.append("startDate", filters.startDate);
  if (filters.endDate) params.append("endDate", filters.endDate);

  const queryString = params.toString();
  const url = `/audit/statistics${queryString ? `?${queryString}` : ""}`;

  return await apiRequest<AuditStatsResponse>(url, "GET");
}

/**
 * Obtém tipos de ação de auditoria
 */
export async function getActionTypes(): Promise<ActionTypesResponse> {
  return await apiRequest<ActionTypesResponse>("/audit/action-types", "GET");
}

/**
 * Obtém logs por tipo de ação
 */
export async function getLogsByActionType(
  actionType: string,
  filters: AuditFilters = {}
): Promise<AuditResponse> {
  const params = new URLSearchParams();

  if (filters.limit) params.append("limit", filters.limit.toString());
  if (filters.offset) params.append("offset", filters.offset.toString());
  if (filters.userId) params.append("userId", filters.userId);
  if (filters.startDate) params.append("startDate", filters.startDate);
  if (filters.endDate) params.append("endDate", filters.endDate);

  const queryString = params.toString();
  const url = `/audit/action-type/${encodeURIComponent(actionType)}${
    queryString ? `?${queryString}` : ""
  }`;

  return await apiRequest<AuditResponse>(url, "GET");
}

/**
 * Formata dados de auditoria para exibição
 */
export function formatAuditLog(log: AuditLog) {
  let parsedDetails = null;
  if (log.details) {
    try {
      parsedDetails = JSON.parse(log.details);
    } catch (error) {
      parsedDetails = { description: log.details };
    }
  }

  return {
    ...log,
    formattedDate: new Date(log.created_at || new Date()).toLocaleString(
      "pt-BR",
      {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }
    ),
    parsedDetails,
    actionTypeColor: getActionTypeColor(log.action_type_name || "Desconhecido"),
    actionTypeIcon: getActionTypeIcon(log.action_type_name || "Desconhecido"),
  };
}

/**
 * Obtém cor para o tipo de ação
 */
function getActionTypeColor(actionType: string): string {
  const colorMap: Record<string, string> = {
    Login: "bg-green-100 text-green-800",
    Logout: "bg-red-100 text-red-800",
    "Acesso API": "bg-blue-100 text-blue-800",
    Criação: "bg-purple-100 text-purple-800",
    Atualização: "bg-yellow-100 text-yellow-800",
    Exclusão: "bg-red-100 text-red-800",
    Configuração: "bg-gray-100 text-gray-800",
    Exportação: "bg-indigo-100 text-indigo-800",
    Importação: "bg-pink-100 text-pink-800",
  };

  return colorMap[actionType] || "bg-gray-100 text-gray-800";
}

/**
 * Obtém ícone para o tipo de ação
 */
function getActionTypeIcon(actionType: string): string {
  const iconMap: Record<string, string> = {
    Login: "🔐",
    Logout: "🚪",
    "Acesso API": "🌐",
    Criação: "➕",
    Atualização: "✏️",
    Exclusão: "🗑️",
    Configuração: "⚙️",
    Exportação: "📤",
    Importação: "📥",
  };

  return iconMap[actionType] || "📋";
}
